import { defineComponent as _defineComponent } from 'vue'
import { computed, getCurrentInstance, onMounted, ref, watch } from "vue";
import VModal from "@/components/vModal.vue";
import vSelect from "@/components/vSelect.vue";
import draggable from "vuedraggable";
import Country from "@/models/Country";
import GeneralRepository from "@/utils/API/GeneralRepository";
import RegulatoryNotificationRepository, { type RegulatoryNotificationItem } from "@/utils/API/RegulatoryNotificationRepository";
import {
  createEmptyRegulatoryAnnouncement,
  type RegulatoryAnnouncement,
} from "@/utils/regulatoryUpdates";

type RegulatoryAdminTab = "regulatory" | "announcement" | "feature";

export default /*#__PURE__*/_defineComponent({
  __name: 'RegulatoryUpdates',
  setup(__props) {

const regulatoryCountryNames = new Set(["United States", "Canada", "United Kingdom"]);

const availableCountries = ref<Country[]>([]);
const items = ref<RegulatoryNotificationItem[]>([]);
const activeCountryKey = ref("");
const activeTab = ref<RegulatoryAdminTab>("regulatory");
const showAnnouncementModal = ref(false);
const showDeleteConfirmModal = ref(false);
const editingAnnouncementId = ref<number | null>(null);
const pendingDeleteAnnouncementId = ref<number | null>(null);
const announcementForm = ref<RegulatoryAnnouncement>(createEmptyRegulatoryAnnouncement());
const vm = getCurrentInstance();

function getFirstCountryKey() {
  return availableCountries.value[0]?.id?.toString() || "";
}

function resolveCountryKey(value: unknown) {
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }

  if (value && typeof value === "object") {
    const option = value as { id?: string | number };
    if (option.id != null) {
      return String(option.id);
    }
  }

  return "";
}

function ensureActiveCountryKey(preferredKey = activeCountryKey.value) {
  const resolvedKey = resolveCountryKey(preferredKey);
  const matchedCountry = availableCountries.value.find((country) => String(country.id) === resolvedKey);
  activeCountryKey.value = matchedCountry?.id?.toString() || getFirstCountryKey();
}

const activeCountry = computed(() => {
  return availableCountries.value.find((country) => String(country.id) === activeCountryKey.value)
    || availableCountries.value[0]
    || null;
});

function mapItemToAnnouncement(item: RegulatoryNotificationItem): RegulatoryAnnouncement {
  return {
    category: item.type === "Notification" ? "announcement" : item.type === "Feature" ? "feature" : "regulatory",
    id: item.id,
    subject: item.title,
    summary: item.summary,
    link: item.url,
    isVisible: item.isVisible,
    isNew: item.isNew,
    displayOrder: item.displayOrder,
    countryId: item.countryId,
  };
}

function getItemTypeForTab(tab: RegulatoryAdminTab = activeTab.value) {
  if (tab === "announcement") {
    return "Notification" as const;
  }

  if (tab === "feature") {
    return "Feature" as const;
  }

  return "Regulatory" as const;
}

const activeTabAnnouncements = computed({
  get() {
    if (activeTab.value !== "regulatory") {
      return items.value
        .filter((item) => item.type === getItemTypeForTab())
        .sort((left, right) => {
          const leftOrder = left.displayOrder > 0 ? left.displayOrder : Number.MAX_SAFE_INTEGER;
          const rightOrder = right.displayOrder > 0 ? right.displayOrder : Number.MAX_SAFE_INTEGER;
          return leftOrder - rightOrder || right.id - left.id;
        })
        .map(mapItemToAnnouncement);
    }

    return items.value
      .filter((item) => item.type === "Regulatory" && String(item.countryId) === activeCountryKey.value)
      .sort((left, right) => left.displayOrder - right.displayOrder || right.id - left.id)
      .map(mapItemToAnnouncement);
  },
  set(value: RegulatoryAnnouncement[]) {
    if (activeTab.value !== "regulatory") {
      return;
    }

    const regulatoryIds = new Set(value.map((item) => item.id));
    const untouchedItems = items.value.filter((item) => !regulatoryIds.has(item.id));
    const reorderedItems = value.map((item, index) => ({
      ...item,
      displayOrder: index + 1,
    })).map((item) => ({
      id: item.id,
      countryId: item.countryId ?? null,
      countryName: "",
      type: getItemTypeForTab(),
      title: item.subject,
      summary: item.summary,
      url: item.link,
      isVisible: item.isVisible,
      isNew: item.isNew,
      displayOrder: item.displayOrder,
      createdOnUtc: "",
      updatedOnUtc: null,
    }));

    items.value = [...untouchedItems, ...reorderedItems];
  },
});

const activeTabTitle = computed(() => {
  if (activeTab.value === "announcement") {
    return "Announcements";
  }

  if (activeTab.value === "feature") {
    return "Features";
  }

  return "Regulatory changes";
});

const activeTabButtonLabel = computed(() => {
  if (activeTab.value === "announcement") {
    return "Add announcement";
  }

  if (activeTab.value === "feature") {
    return "Add feature";
  }

  return "Add regulatory changes";
});

const showOrderColumn = computed(() => activeTab.value === "regulatory");
const showNewColumn = computed(() => activeTab.value === "regulatory");
const canAddActiveTabItem = computed(() => activeTab.value !== "feature" || activeTabAnnouncements.value.length < 3);

const countryOptions = computed(() => {
  return availableCountries.value.map((country) => ({
    id: country.id,
    name: country.name,
  }));
});

async function loadItems() {
  const [regulatoryResponse, notificationResponse, featureResponse] = await Promise.all([
    RegulatoryNotificationRepository.list("Regulatory"),
    RegulatoryNotificationRepository.list("Notification"),
    RegulatoryNotificationRepository.list("Feature"),
  ]);

  const regulatoryItems = Array.isArray(regulatoryResponse.data?.result) ? regulatoryResponse.data.result : [];
  const notificationItems = Array.isArray(notificationResponse.data?.result) ? notificationResponse.data.result : [];
  const featureItems = Array.isArray(featureResponse.data?.result) ? featureResponse.data.result : [];
  items.value = [...regulatoryItems, ...notificationItems, ...featureItems];
}

function resetAnnouncementForm() {
  announcementForm.value = {
    ...createEmptyRegulatoryAnnouncement(),
    category: activeTab.value,
    isVisible: activeTab.value !== "announcement",
    isNew: false,
    countryId: activeTab.value === "regulatory" ? Number(activeCountryKey.value) : null,
  };
}

function handleCountryChange(value: string | number | null) {
  ensureActiveCountryKey(value);
}

function openCreateAnnouncementModal() {
  editingAnnouncementId.value = null;
  resetAnnouncementForm();
  showAnnouncementModal.value = true;
}

function openEditAnnouncementModal(announcementId: number) {
  const matchedAnnouncement = activeTabAnnouncements.value.find((item) => item.id === announcementId);
  if (!matchedAnnouncement) {
    return;
  }

  editingAnnouncementId.value = announcementId;
  announcementForm.value = {
    ...matchedAnnouncement,
    isNew: activeTab.value === "regulatory" ? matchedAnnouncement.isNew : false,
  };
  showAnnouncementModal.value = true;
}

function closeAnnouncementModal() {
  showAnnouncementModal.value = false;
  editingAnnouncementId.value = null;
  resetAnnouncementForm();
}

async function saveAnnouncement() {
  if (activeTab.value === "feature" && !editingAnnouncementId.value && !canAddActiveTabItem.value) {
    vm?.proxy?.$snotify?.error("You can add up to three feature items.");
    return;
  }

  const displayOrder = activeTab.value === "announcement"
    ? 0
    : (announcementForm.value.displayOrder || activeTabAnnouncements.value.length + (editingAnnouncementId.value ? 0 : 1));

  try {
    await RegulatoryNotificationRepository.save({
      id: editingAnnouncementId.value || 0,
      countryId: activeTab.value === "regulatory" ? Number(activeCountryKey.value) : null,
      type: getItemTypeForTab(),
      title: announcementForm.value.subject,
      summary: announcementForm.value.summary,
      url: announcementForm.value.link,
      isVisible: activeTab.value === "feature" ? true : activeTab.value === "announcement" ? true : announcementForm.value.isVisible,
      isNew: activeTab.value === "regulatory" ? announcementForm.value.isNew : false,
      displayOrder,
    });

    await loadItems();
    closeAnnouncementModal();
  }
  catch (error: any) {
    const message = error?.response?.data?.errorMessages?.[0]?.userVisibleMessage
      || error?.response?.data?.message
      || "Unable to save the selected item.";
    vm?.proxy?.$snotify?.error(message);
  }
}

async function removeAnnouncement(announcementId: number) {
  try {
    await RegulatoryNotificationRepository.delete(announcementId);
    await loadItems();
  }
  catch (error: any) {
    const message = error?.response?.data?.errorMessages?.[0]?.userVisibleMessage
      || "Unable to delete the selected item.";
    vm?.proxy?.$snotify?.error(message);
  }
}

function requestRemoveAnnouncement(announcementId: number) {
  pendingDeleteAnnouncementId.value = announcementId;
  showDeleteConfirmModal.value = true;
}

function cancelRemoveAnnouncement() {
  showDeleteConfirmModal.value = false;
  pendingDeleteAnnouncementId.value = null;
}

async function confirmRemoveAnnouncement() {
  if (!pendingDeleteAnnouncementId.value) {
    cancelRemoveAnnouncement();
    return;
  }

  const announcementId = pendingDeleteAnnouncementId.value;
  cancelRemoveAnnouncement();
  await removeAnnouncement(announcementId);
}

async function persistRegulatoryOrder() {
  if (activeTab.value !== "regulatory") {
    return;
  }

  await RegulatoryNotificationRepository.reorder(
    "Regulatory",
    Number(activeCountryKey.value),
    activeTabAnnouncements.value.map((item, index) => ({
      id: item.id,
      displayOrder: index + 1,
    }))
  );
  await loadItems();
}

async function toggleItemFlag(item: RegulatoryAnnouncement) {
  const isFeature = item.category === "feature";
  await RegulatoryNotificationRepository.save({
    id: item.id,
    countryId: item.countryId ?? null,
    type: item.category === "announcement" ? "Notification" : isFeature ? "Feature" : "Regulatory",
    title: item.subject,
    summary: item.summary,
    url: item.link,
    isVisible: isFeature ? true : item.isVisible,
    isNew: item.isNew,
    displayOrder: item.displayOrder,
  });
}

onMounted(async() => {
  try {
    const response = await GeneralRepository.listCountries();
    availableCountries.value = (response.data.result || []).filter((country: Country) => regulatoryCountryNames.has(country.name));
    ensureActiveCountryKey();
    await loadItems();
  }
  catch {
    availableCountries.value = [];
  }
});

watch(activeCountryKey, () => {
  ensureActiveCountryKey();
});

return { __sfc: true,regulatoryCountryNames, availableCountries, items, activeCountryKey, activeTab, showAnnouncementModal, showDeleteConfirmModal, editingAnnouncementId, pendingDeleteAnnouncementId, announcementForm, vm, getFirstCountryKey, resolveCountryKey, ensureActiveCountryKey, activeCountry, mapItemToAnnouncement, getItemTypeForTab, activeTabAnnouncements, activeTabTitle, activeTabButtonLabel, showOrderColumn, showNewColumn, canAddActiveTabItem, countryOptions, loadItems, resetAnnouncementForm, handleCountryChange, openCreateAnnouncementModal, openEditAnnouncementModal, closeAnnouncementModal, saveAnnouncement, removeAnnouncement, requestRemoveAnnouncement, cancelRemoveAnnouncement, confirmRemoveAnnouncement, persistRegulatoryOrder, toggleItemFlag, VModal, vSelect, draggable }
}

})